// ============================================================
// Page Activité du compte — Lot L23 (frontend client)
// ============================================================
// Vue scopée au commerce de l'utilisateur connecté. Le propriétaire
// (et l'admin) peut consulter qui a fait quoi sur son commerce.
// Les gérants n'ont pas accès à cette page.
// ============================================================

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  ScrollText, RefreshCw, Download, ChevronLeft, ChevronRight,
  Search, Loader, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

const PAR_PAGE = 50;

function formatDateHeure(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR')} ${d.toLocaleTimeString('fr-FR',
    { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

function couleurAction(action) {
  if (!action) return 'text-gray-400';
  if (/cancel|delete|annul|desactiv|reset|revok|suppr/i.test(action)) return 'text-red-400';
  if (/create|add|new/i.test(action))                                 return 'text-emerald-400';
  if (/update|modif|edit|change/i.test(action))                       return 'text-amber-400';
  if (/login|auth|2fa/i.test(action))                                 return 'text-blue-400';
  return 'text-gray-300';
}

export default function ActiviteCompte() {
  const { canEditParametres } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [typesAction, setTypesAction] = useState([]);
  const [selected, setSelected] = useState(null);

  const [filtres, setFiltres] = useState({
    action: '',
    entite_type: '',
    date_debut: '',
    date_fin: '',
  });

  const params = useMemo(() => {
    const p = { page, limit: PAR_PAGE };
    Object.entries(filtres).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [page, filtres]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/audit-logs', { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Échec du chargement');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { charger(); }, [charger]);

  useEffect(() => {
    api.get('/audit-logs/types').then((r) => setTypesAction(r.data || [])).catch(() => {});
  }, []);

  const exporter = async () => {
    setExporting(true);
    try {
      const res = await api.get('/audit-logs/export.csv', {
        params: { ...params, page: undefined, limit: undefined },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `activite-compte-${new Date().toISOString().substring(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export téléchargé');
    } catch (err) {
      toast.error('Échec de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const reset = () => {
    setFiltres({ action: '', entite_type: '', date_debut: '', date_fin: '' });
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAR_PAGE));

  // Garde côté UI (la route backend renforce déjà avec readOwner)
  if (!canEditParametres) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="text-amber-400" size={20} />
        <p className="text-gray-300 text-sm">
          Cette page est réservée aux propriétaires.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-yellow-500/15 border border-yellow-500/30 rounded-lg flex items-center justify-center">
            <ScrollText size={18} className="text-yellow-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Activité du compte</h1>
            <p className="text-xs text-gray-500">
              Historique des actions sensibles sur ton commerce. Conservé indéfiniment.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={charger}
            className="p-2 text-gray-400 hover:bg-gray-800 hover:text-white rounded-lg"
            title="Rafraîchir"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={exporter}
            disabled={exporting || logs.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-600
                       disabled:opacity-40 text-gray-900 text-sm font-medium rounded-lg"
          >
            {exporting ? <Loader size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
        </div>
      </header>

      {/* Filtres */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-3
                      grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Action</label>
          <select
            value={filtres.action}
            onChange={(e) => { setFiltres((f) => ({ ...f, action: e.target.value })); setPage(1); }}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white"
          >
            <option value="">Toutes</option>
            {typesAction.slice(0, 50).map((t) => (
              <option key={t.action} value={t.action}>{t.action} ({t.nb})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Type d'entité</label>
          <input
            type="text"
            value={filtres.entite_type}
            onChange={(e) => { setFiltres((f) => ({ ...f, entite_type: e.target.value })); setPage(1); }}
            placeholder="ex: vente, client"
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Du</label>
          <input
            type="date"
            value={filtres.date_debut}
            onChange={(e) => { setFiltres((f) => ({ ...f, date_debut: e.target.value })); setPage(1); }}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Au</label>
          <input
            type="date"
            value={filtres.date_fin}
            onChange={(e) => { setFiltres((f) => ({ ...f, date_fin: e.target.value })); setPage(1); }}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white"
          />
        </div>
        <div className="col-span-2 sm:col-span-4 flex justify-end">
          <button
            onClick={reset}
            className="text-xs text-gray-400 hover:text-white px-3 py-1"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-800/50">
              <tr className="text-left text-xs text-gray-400">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Utilisateur</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Entité</th>
                <th className="px-3 py-2 font-medium">Motif</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center text-gray-500 py-8">
                  <Loader size={20} className="animate-spin mx-auto" />
                </td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-gray-500 py-8">
                  Aucune activité ne correspond aux filtres
                </td></tr>
              ) : (
                logs.map((l) => (
                  <tr
                    key={l.id}
                    onClick={() => setSelected(l)}
                    className="border-t border-gray-800 hover:bg-gray-800/30 cursor-pointer"
                  >
                    <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
                      {formatDateHeure(l.created_at)}
                    </td>
                    <td className="px-3 py-2 text-gray-300">
                      {l.user_nom || l.user_email || '—'}
                    </td>
                    <td className={`px-3 py-2 font-mono text-xs ${couleurAction(l.action)}`}>
                      {l.action}
                    </td>
                    <td className="px-3 py-2 text-gray-400 text-xs">
                      {l.entite_type}{l.entite_id ? ` · ${l.entite_id.substring(0, 8)}` : ''}
                    </td>
                    <td className="px-3 py-2 text-gray-400 text-xs max-w-xs truncate">
                      {l.motif || '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-800 text-xs text-gray-400">
          <span>
            {total.toLocaleString('fr-FR')} entrées · page {page} / {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="p-2 hover:bg-gray-800 rounded disabled:opacity-30"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
              className="p-2 hover:bg-gray-800 rounded disabled:opacity-30"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-gray-900 border border-gray-700 rounded-xl max-w-2xl w-full p-5 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Search size={16} /> Détail de l'événement
              </h3>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-400 hover:text-white text-sm"
              >
                Fermer
              </button>
            </div>
            <dl className="space-y-2 text-sm">
              <Detail label="Date"        value={formatDateHeure(selected.created_at)} />
              <Detail label="Action"      value={<span className={couleurAction(selected.action) + ' font-mono'}>{selected.action}</span>} />
              <Detail label="Utilisateur" value={selected.user_nom ? `${selected.user_nom} (${selected.user_email})` : (selected.user_email || '—')} />
              <Detail label="Entité"      value={`${selected.entite_type || '—'}${selected.entite_id ? ' · ' + selected.entite_id : ''}`} />
              <Detail label="Motif"       value={selected.motif || '—'} />
              <Detail label="IP"          value={selected.ip || '—'} />
            </dl>
            {selected.anciennes_valeurs && (
              <details className="mt-3">
                <summary className="text-xs text-gray-400 cursor-pointer hover:text-white">Anciennes valeurs</summary>
                <pre className="mt-2 bg-gray-800 rounded p-2 text-xs text-gray-300 overflow-x-auto">
                  {JSON.stringify(selected.anciennes_valeurs, null, 2)}
                </pre>
              </details>
            )}
            {selected.nouvelles_valeurs && (
              <details className="mt-3" open>
                <summary className="text-xs text-gray-400 cursor-pointer hover:text-white">Nouvelles valeurs</summary>
                <pre className="mt-2 bg-gray-800 rounded p-2 text-xs text-gray-300 overflow-x-auto">
                  {JSON.stringify(selected.nouvelles_valeurs, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="text-xs text-gray-500 w-28 shrink-0">{label}</dt>
      <dd className="text-sm text-gray-200">{value}</dd>
    </div>
  );
}
